import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

export default function SlideshowSettingsDialog({ projectId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [durationSeconds, setDurationSeconds] = useState(5);
  const [transition, setTransition] = useState('fade');
  const [showCaptions, setShowCaptions] = useState(true);
  const [sortOrder, setSortOrder] = useState('asc');
  const [musicUrl, setMusicUrl] = useState('');

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/slideshow-settings`)
      .then((res) => {
        const s = res.data || {};
        setEnabled(s.enabled !== false);
        setDurationSeconds(s.duration_seconds || 5);
        setTransition(s.transition || 'fade');
        setShowCaptions(s.show_captions !== false);
        setSortOrder(s.sort_order || 'asc');
        setMusicUrl(s.music_url || '');
      })
      .catch(() => toast.error('Failed to load slideshow settings'))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled,
        duration_seconds: parseInt(durationSeconds, 10) || 5,
        transition,
        show_captions: showCaptions,
        sort_order: sortOrder,
        music_url: musicUrl.trim() || null,
      };
      const res = await axios.put(`${API}/projects/${projectId}/slideshow-settings`, payload);
      toast.success('Slideshow settings saved');
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="slideshow-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Settings className="w-5 h-5 text-blue-600" />
            Slideshow Settings
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Configure how the slideshow plays for this project.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            {/* Enabled toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-semibold text-gray-900">Slideshow available to clients</Label>
                <p className="text-xs text-gray-500 mt-0.5">When ON, clients see a "Play Slideshow" button.</p>
              </div>
              <button
                type="button"
                onClick={() => setEnabled(!enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                data-testid="slideshow-enabled-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Duration */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Slide duration</Label>
              <div className="flex gap-2 mt-1.5" data-testid="slideshow-duration-options">
                {[3, 5, 8, 12].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setDurationSeconds(s)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                      durationSeconds === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {s}s
                  </button>
                ))}
              </div>
            </div>

            {/* Transition */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Transition</Label>
              <div className="flex gap-2 mt-1.5">
                {[
                  { v: 'fade', l: 'Fade' },
                  { v: 'none', l: 'None' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setTransition(opt.v)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                      transition === opt.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort order */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Photo order</Label>
              <div className="flex gap-2 mt-1.5">
                {[
                  { v: 'asc', l: 'Oldest first' },
                  { v: 'desc', l: 'Newest first' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setSortOrder(opt.v)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                      sortOrder === opt.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Background music */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Background music (YouTube link)</Label>
              <input
                type="url"
                value={musicUrl}
                onChange={(e) => setMusicUrl(e.target.value)}
                placeholder="e.g. https://www.youtube.com/watch?v=..."
                className="w-full mt-1.5 px-3 py-2 rounded-lg text-sm border border-gray-200 focus:border-blue-400 focus:outline-none text-gray-900 bg-white"
                data-testid="slideshow-music-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Clients get a music button in the slideshow to play your song if they wish. Leave empty for no music.
              </p>
            </div>

            {/* Show captions */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label className="text-sm font-semibold text-gray-700">Show captions on slides</Label>
              <button
                type="button"
                onClick={() => setShowCaptions(!showCaptions)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showCaptions ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showCaptions ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-slideshow-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
